<script setup lang="ts">
import { ref, onUnmounted, watch } from 'vue'

const props = defineProps<{
  isVisible: boolean
}>()

const emit = defineEmits<{
  (e: 'retry'): void
}>()

const isPlaying = ref(false)
const score = ref(0)
const highScore = ref(0)
const gameCanvas = ref<HTMLCanvasElement | null>(null)
let ctx: CanvasRenderingContext2D | null = null
let snake = [{ x: 10, y: 10 }]
let food = { x: 15, y: 15 }
let dx = 1
let dy = 0
let gameLoop: number | null = null
let gameTimeout: ReturnType<typeof setTimeout> | null = null

const GRID_SIZE = 20
const CANVAS_SIZE = 300
const CELL_SIZE = CANVAS_SIZE / GRID_SIZE

function startGame() {
  if (!gameCanvas.value || !ctx) return

  // Initialize canvas background first
  ctx.fillStyle = '#1a1a1a'
  ctx.fillRect(0, 0, CANVAS_SIZE, CANVAS_SIZE)

  isPlaying.value = true
  score.value = 0
  snake = [{ x: 10, y: 10 }]
  food = getRandomFood()
  dx = 1
  dy = 0

  if (gameTimeout) clearTimeout(gameTimeout)
  if (gameLoop) cancelAnimationFrame(gameLoop)

  // Start the game loop
  gameStep()
}

function gameStep() {
  if (!ctx || !gameCanvas.value || !isPlaying.value) return

  gameTimeout = setTimeout(() => {
    // Clear the canvas
    ctx!.fillStyle = '#1a1a1a'
    ctx!.fillRect(0, 0, CANVAS_SIZE, CANVAS_SIZE)

    // Move snake
    const head = { x: snake[0].x + dx, y: snake[0].y + dy }

    // Check walls
    if (head.x < 0) head.x = GRID_SIZE - 1
    if (head.x >= GRID_SIZE) head.x = 0
    if (head.y < 0) head.y = GRID_SIZE - 1
    if (head.y >= GRID_SIZE) head.y = 0

    // Check self-collision
    if (snake.some((segment) => segment.x === head.x && segment.y === head.y)) {
      gameOver()
      return
    }

    snake.unshift(head)

    // Check food collision
    if (head.x === food.x && head.y === food.y) {
      score.value += 10
      food = getRandomFood()
    } else {
      snake.pop()
    }

    // Draw snake
    ctx!.fillStyle = '#4ade80'
    snake.forEach((segment) => {
      ctx!.fillRect(
        segment.x * CELL_SIZE,
        segment.y * CELL_SIZE,
        CELL_SIZE - 1,
        CELL_SIZE - 1,
      )
    })

    // Draw food
    ctx!.fillStyle = '#ef4444'
    ctx!.fillRect(
      food.x * CELL_SIZE,
      food.y * CELL_SIZE,
      CELL_SIZE - 1,
      CELL_SIZE - 1,
    )

    if (isPlaying.value) {
      gameLoop = requestAnimationFrame(gameStep)
    }
  }, 100) // Control game speed
}

function getRandomFood() {
  return {
    x: Math.floor(Math.random() * GRID_SIZE),
    y: Math.floor(Math.random() * GRID_SIZE),
  }
}

function gameOver() {
  isPlaying.value = false
  if (score.value > highScore.value) {
    highScore.value = score.value
  }
  if (gameTimeout) clearTimeout(gameTimeout)
  if (gameLoop) cancelAnimationFrame(gameLoop)
}

function handleKeydown(e: KeyboardEvent) {
  if (!isPlaying.value) return

  switch (e.key) {
    case 'ArrowUp':
      if (dy !== 1) {
        dx = 0
        dy = -1
      }
      break
    case 'ArrowDown':
      if (dy !== -1) {
        dx = 0
        dy = 1
      }
      break
    case 'ArrowLeft':
      if (dx !== 1) {
        dx = -1
        dy = 0
      }
      break
    case 'ArrowRight':
      if (dx !== -1) {
        dx = 1
        dy = 0
      }
      break
  }
}

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeydown)
  if (gameTimeout) clearTimeout(gameTimeout)
  if (gameLoop) cancelAnimationFrame(gameLoop)
})

watch(gameCanvas, (canvas) => {
  if (canvas) {
    ctx = canvas.getContext('2d')
    if (ctx) {
      ctx.fillStyle = '#1a1a1a'
      ctx.fillRect(0, 0, CANVAS_SIZE, CANVAS_SIZE)
    }
    window.addEventListener('keydown', handleKeydown)
  } else {
    isPlaying.value = false
    if (gameTimeout) clearTimeout(gameTimeout)
    if (gameLoop) cancelAnimationFrame(gameLoop)
    window.removeEventListener('keydown', handleKeydown)
    ctx = null
  }
})
</script>

<template>
  <Transition
    enter-active-class="transition duration-300 ease-out"
    enter-from-class="transform opacity-0"
    enter-to-class="opacity-100"
    leave-active-class="transition duration-200 ease-in"
    leave-from-class="opacity-100"
    leave-to-class="transform opacity-0"
  >
    <div
      v-if="isVisible"
      class="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 backdrop-blur-sm"
    >
      <div class="mx-4 max-w-md rounded-lg bg-white p-6 text-center shadow-xl">
        <h2 class="mb-4 text-xl font-bold text-gray-900">
          System Temporarily Unavailable
        </h2>
        <p class="mb-6 text-gray-600">
          We're experiencing technical difficulties. Please try again in a few
          moments.
        </p>
        <button
          @click="emit('retry')"
          class="mb-8 rounded-md bg-primary-600 px-4 py-2 text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
        >
          Try Again
        </button>

        <!-- Snake Game Section -->
        <div class="mt-6 border-t border-gray-200 pt-6">
          <h3 class="mb-2 text-sm font-medium text-gray-700">
            While you wait, why not play a quick game?
          </h3>
          <div class="flex justify-center">
            <div class="text-center">
              <canvas
                ref="gameCanvas"
                :width="CANVAS_SIZE"
                :height="CANVAS_SIZE"
                class="mb-4 rounded border border-gray-200"
              ></canvas>
              <div class="mb-4 flex justify-between text-sm text-gray-600">
                <span>Score: {{ score }}</span>
                <span>High Score: {{ highScore }}</span>
              </div>
              <button
                @click="startGame"
                class="rounded-md bg-green-500 px-4 py-2 text-white hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
              >
                {{ isPlaying ? 'Restart Game' : 'Start Game' }}
              </button>
              <p class="mt-2 text-xs text-gray-500">
                Use arrow keys to control the snake
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>
